import React from 'react'
import useSWRMutation from 'swr/mutation'

import fetcher from '../../api/fetcher.mjs'
import config from '../../config'
import { useAlerts } from '../../context/alerts'
import { useAuth } from '../../context/auth'
import Spinner from '../ui/spinner'
import Button, { ButtonSize, ButtonVariant } from '../ui/button'
import { ReactComponent as PlusIcon} from '../../static/images/plus.svg'
import './logoEditor.scss'

type Props = {
  imageSrc?: string
  editable?: boolean
  compact?: boolean
  /** When set, uploads stay local and base64 is returned instead of calling the API. */
  onLogoChange?: (logoBase64: string) => void | Promise<void>
}

async function uploadLogo(url: string, { arg }: { arg: { logo: string } }) {
  return fetcher(`${config.REACT_APP_API_URL}${url}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(arg),
  })
}

const acceptedFormats = 'image/png,image/jpeg,image/jpg,image/svg+xml,image/webp,image/avif'
const fileLimit = 1e6

const BusinessLogoEditor: React.FC<Props> = ({
  imageSrc,
  editable = true,
  compact = false,
  onLogoChange,
}) => {
  const { addAlert } = useAlerts()
  const { mutate } = useAuth()
  const isLocalMode = !!onLogoChange
  const [previewImage, setPreviewImage] = React.useState(imageSrc || '')
  const [isDragging, setIsDragging] = React.useState(false)
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    setPreviewImage(imageSrc || '')
  }, [imageSrc])

  const { trigger, isMutating } = useSWRMutation('/api/user/business/logo', uploadLogo)

  const convertFileToBase64 = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = reject
    })
  }

  const validateFile = (file: File) => {
    if (file.size > fileLimit) {
      addAlert({ type: 'error', message: 'File size should be less than 1MB' })
      return false
    }
    return true
  }

  const uploadFile = async (file: File) => {
    if (!validateFile(file)) return
    try {
      const logoBase64 = await convertFileToBase64(file)
      const previewUrl = URL.createObjectURL(file)
      setPreviewImage(previewUrl)
      if (isLocalMode) {
        await onLogoChange?.(logoBase64)
        return
      }
      await trigger({ logo: logoBase64 })
      await mutate()
      addAlert({ type: 'info', message: 'Your business logo has been updated' })
    } catch (error: any) {
      addAlert({ type: 'error', message: error?.message || 'Could not upload logo' })
      setPreviewImage(imageSrc || '')
    }
  }

  const onInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    await uploadFile(file)
    event.target.value = ''
  }

  const openFileFinder = () => {
    if (!editable || isMutating) return
    fileInputRef.current?.click()
  }

  const onDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(false)
    if (!editable || isMutating) return
    const file = event.dataTransfer.files?.[0]
    if (!file) return
    await uploadFile(file)
  }

  return (
    <div
      className={`business-logo-editor ${compact ? 'business-logo-editor--compact' : ''} ${previewImage ? 'business-logo-editor--has-logo' : ''} ${isDragging ? 'business-logo-editor--dragging' : ''}`}
      onClick={openFileFinder}
      onDragOver={(e) => {
        if (!editable || isMutating) return
        e.preventDefault()
        setIsDragging(true)
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={onDrop}
    >
      <input
        ref={fileInputRef}
        type='file'
        className='d-none'
        accept={acceptedFormats}
        onChange={onInputChange}
        disabled={!editable || isMutating}
      />
      {isMutating ? (
        <div className='business-logo-editor__loading'>
          <Spinner />
        </div>
      ) : previewImage ? (
        <img src={previewImage} alt='Business logo' className='business-logo-editor__image' />
      ) : (
        <div className='business-logo-editor__empty'>
          {editable ? (
            <Button
              type='button'
              variant={ButtonVariant.LIGHT}
              size={ButtonSize.SMALL}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
                openFileFinder()
              }}
              icon={<PlusIcon width={14} height={14} />}
              iconPosition='start'
            >
              Your logo
            </Button>
          ) : (
            <span className='text-muted small'>No logo</span>
          )}
        </div>
      )}
    </div>
  )
}

export default BusinessLogoEditor

